import React from "react";
import { useFormContext } from "react-hook-form";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiSelect,
  MuiTextField
} from "../../../../../../components";
import { PAGE_IDS } from "../../../../../../helpers/constants";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { InheritDocumentsSearchFormKey } from "../../../../modelsAndSchemas/Models";
import { inheritDocumentSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { InheritDocumentsSearchFormWrapper } from "./InheritDocumentsSearchForm.style";

const pageId = PAGE_IDS.INHERIT_DOCUMENTS_SEARCH;

interface InheritDocumentsSearchFormProps {
  handleClearAllSearch: () => void;
  handleSearchClick: () => void;
}

export const InheritDocumentsSearchForm: React.FC<
  InheritDocumentsSearchFormProps
> = ({ handleClearAllSearch, handleSearchClick }) => {
  const { isMobile } = useMediaQueries();
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const { useFetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();
 
  const {
    inheritDocumentTitle,
    searchDocumentName,
    searchContractID,
    searchDocumentType,
    documentTypeList,
  } = inheritDocumentSiteCoreKeys;
    
  const getLabel = useFetchLabel(inheritDocumentTitle);
  const { commonARIALabel } = useNavigationSiteCore();
  const { control } = useFormContext();

  return (
    <InheritDocumentsSearchFormWrapper>
      <AccordionHeader
        showExpandCollapseIcon={false}
        title={<AccordionHeaderText label={commonLabels.search} />}
        isExpanded={!isMobile}
      >
        <form data-testid="inherit-documents-search-form">
          <MuiGrid container className="form-container">
            <MuiGrid size={gridSize} className="first-row">
              <FormController
                control={control}
                name={InheritDocumentsSearchFormKey.documentName}
              >
                <MuiTextField
                  id={`${pageId}-${InheritDocumentsSearchFormKey.documentName}`}
                  label={getLabel(searchDocumentName)}
                  placeholder={commonLabels.inputPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className="first-row">
              <FormController
                control={control}
                name={InheritDocumentsSearchFormKey.contractId}
              >
                <MuiTextField
                  label={getLabel(searchContractID)}
                  placeholder={commonLabels.inputPlaceHolder}
                  id={`${pageId}-${InheritDocumentsSearchFormKey.contractId}`}
                  requiredARIALabel={commonARIALabel.required}
                  type="number"
                  inputNumberWithoutDecimal
                  positiveValuesOnly
                />
              </FormController>
            </MuiGrid>

            <MuiGrid
              size={gridSize}
              className={gridSizeLargeClass}
              spacing={isMobile ? 2 : 0}
            >
              <FormController
                control={control}
                name={InheritDocumentsSearchFormKey.documentType}
              >
                <MuiSelect
                  label={getLabel(searchDocumentType)}
                  options={fetchDropdownValues(
                    inheritDocumentTitle,
                    documentTypeList,
                  )}
                  id={`${pageId}-${InheritDocumentsSearchFormKey.documentType}`}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  showSearch
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiGrid
            spacing={isMobile ? 2 : 0}
            container
            className={"button-container"}
          >
            <MuiButtonSecondary
              onClick={handleClearAllSearch}
              id={`${pageId}-clearFilter`}
              data-testid="clear-filter"
            >
              {commonLabels.clearAll}
            </MuiButtonSecondary>
            <MuiButtonPrimary
              onClick={handleSearchClick}
              id={`${pageId}-search`}
              data-testid="search"
            >
              {commonLabels.search}
            </MuiButtonPrimary>
          </MuiGrid>
        </form>
      </AccordionHeader>
    </InheritDocumentsSearchFormWrapper>
  );
};
